// src/services/diaryService.js
import { db, auth } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  writeBatch,
  serverTimestamp 
} from 'firebase/firestore';
import { firestoreService } from './firestoreService';
import { notificationService } from './notificationService';

/**
 * DiaryService
 * ---------------------------------------------------------
 * Manages all diary-related data under pb-books/{bookId}:
 * - Meetings: pb-books/{bookId}/meetings
 * - Meeting Templates: pb-books/{bookId}/meeting-templates
 * - Contacts: pb-books/{bookId}/contacts
 * - Journal Entries: pb-books/{bookId}/journal-entries
 * - Settings stored in book.diarySettings
 */

export const diaryService = {

  // ============================================================
  // MEETINGS
  // ============================================================

  /**
   * Get a single meeting
   */
  async getMeeting(bookId, meetingId) {
    try {
      const meetingRef = doc(db, 'pb-books', bookId, 'meetings', meetingId);
      const docSnap = await getDoc(meetingRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting meeting:', error);
      throw error;
    }
  },

  /**
   * Save (create or update) a meeting
   */
  async saveMeeting(bookId, meeting) {
    try {
      const meetingId = meeting.id || doc(collection(db, 'pb-books', bookId, 'meetings')).id;
      const meetingRef = doc(db, 'pb-books', bookId, 'meetings', meetingId);
      
      const meetingData = {
        ...meeting,
        id: meetingId,
        bookId,
        createdBy: meeting.createdBy || auth.currentUser?.uid,
        creatorEmail: meeting.creatorEmail || auth.currentUser?.email,
        workspaceId: meeting.workspaceId || null,
        visibility: meeting.visibility || 'workspace',
        updatedAt: serverTimestamp()
      };
      
      if (!meeting.id) {
        meetingData.createdAt = serverTimestamp();
      }
      
      await setDoc(meetingRef, meetingData, { merge: true });
      return meetingId;
    } catch (error) {
      console.error('Error saving meeting:', error);
      throw error;
    }
  },

  /**
   * Delete a meeting
   */
  async deleteMeeting(bookId, meetingId) {
    try {
      const meetingRef = doc(db, 'pb-books', bookId, 'meetings', meetingId);
      await deleteDoc(meetingRef);
      return true;
    } catch (error) {
      console.error('Error deleting meeting:', error);
      throw error;
    }
  },

  /**
   * Get meetings by status
   */
  async getMeetingsByStatus(bookId, status) {
    try {
      const meetingsRef = collection(db, 'pb-books', bookId, 'meetings');
      const q = query(
        meetingsRef, 
        where('status', '==', status),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting meetings by status:', error);
      return [];
    }
  },

  /**
   * Get meetings for a specific date range
   */
  async getMeetingsInRange(bookId, startDate, endDate) {
    try {
      const meetingsRef = collection(db, 'pb-books', bookId, 'meetings');
      const q = query(
        meetingsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
        orderBy('time', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting meetings in range:', error);
      return [];
    }
  },

  /**
   * Update meeting status
   */
  async updateMeetingStatus(bookId, meetingId, status) {
    try {
      const meetingRef = doc(db, 'pb-books', bookId, 'meetings', meetingId);
      await updateDoc(meetingRef, {
        status,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating meeting status:', error);
      throw error;
    }
  },

  // ============================================================
  // MEETING TEMPLATES
  // ============================================================

  /**
   * Get all meeting templates
   */
  async getMeetingTemplates(bookId) {
    try {
      const templatesRef = collection(db, 'pb-books', bookId, 'meeting-templates');
      const q = query(templatesRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting meeting templates:', error);
      return [];
    }
  },

  /**
   * Save a meeting template
   */
  async saveMeetingTemplate(bookId, template) {
    try {
      const templateId = template.id || doc(collection(db, 'pb-books', bookId, 'meeting-templates')).id;
      const templateRef = doc(db, 'pb-books', bookId, 'meeting-templates', templateId);
      
      const templateData = {
        ...template,
        id: templateId,
        bookId,
        createdBy: template.createdBy || auth.currentUser?.uid,
        creatorEmail: template.creatorEmail || auth.currentUser?.email,
        workspaceId: template.workspaceId || null,
        visibility: template.visibility || 'workspace',
        sharedWith: template.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!template.id) {
        templateData.createdAt = serverTimestamp();
      }
      
      await setDoc(templateRef, templateData, { merge: true });
      return templateId;
    } catch (error) {
      console.error('Error saving meeting template:', error);
      throw error;
    }
  },

  /**
   * Delete a meeting template
   */
  async deleteMeetingTemplate(bookId, templateId) {
    try {
      const templateRef = doc(db, 'pb-books', bookId, 'meeting-templates', templateId);
      await deleteDoc(templateRef);
      return true;
    } catch (error) {
      console.error('Error deleting meeting template:', error);
      throw error;
    }
  },

  // ============================================================
  // CONTACTS
  // ============================================================

  /**
   * Get a single contact
   */
  async getContact(bookId, contactId) {
    try {
      const contactRef = doc(db, 'pb-books', bookId, 'contacts', contactId);
      const docSnap = await getDoc(contactRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting contact:', error);
      throw error;
    }
  },

  /**
   * Save (create or update) a contact
   */
  async saveContact(bookId, contact, options = {}) {
    try {
      const contactId = contact.id || doc(collection(db, 'pb-books', bookId, 'contacts')).id;
      const contactRef = doc(db, 'pb-books', bookId, 'contacts', contactId);
      
      const isNew = !contact.id;
      
      const contactData = {
        ...contact,
        id: contactId,
        bookId,
        createdBy: contact.createdBy || auth.currentUser?.uid,
        creatorEmail: contact.creatorEmail || auth.currentUser?.email,
        workspaceId: contact.workspaceId || null,
        visibility: contact.visibility || 'workspace',
        sharedWith: contact.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (isNew) {
        contactData.createdAt = serverTimestamp();
      }
      
      await setDoc(contactRef, contactData, { merge: true });
      
      // Send notification for new contacts with email
      if (isNew && contact.email && options.sendNotification !== false) {
        notificationService.sendContactAddedNotification(
          bookId, 
          contact, 
          options.workspaceName
        ).catch(err => console.warn('Contact notification failed:', err));
      }
      
      return contactId;
    } catch (error) {
      console.error('Error saving contact:', error);
      throw error;
    }
  },

  /**
   * Delete a contact
   */
  async deleteContact(bookId, contactId) {
    try {
      const contactRef = doc(db, 'pb-books', bookId, 'contacts', contactId);
      await deleteDoc(contactRef);
      return true;
    } catch (error) {
      console.error('Error deleting contact:', error);
      throw error;
    }
  },

  /**
   * Get contacts by category
   */
  async getContactsByCategory(bookId, category) {
    try {
      const contactsRef = collection(db, 'pb-books', bookId, 'contacts');
      const q = query(
        contactsRef, 
        where('category', '==', category),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting contacts by category:', error);
      return [];
    }
  },

  /**
   * Search contacts by name or email
   */
  async searchContacts(bookId, searchTerm) {
    try {
      // Firestore doesn't support full-text search, so we get all and filter client-side
      const contacts = await this.getContacts(bookId);
      const term = searchTerm.toLowerCase();
      
      return contacts.filter(c => 
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.phone?.includes(term) ||
        c.company?.toLowerCase().includes(term)
      );
    } catch (error) {
      console.error('Error searching contacts:', error);
      return [];
    }
  },

  // ============================================================
  // JOURNAL ENTRIES
  // ============================================================

  /**
   * Get a single journal entry
   */
  async getJournalEntry(bookId, entryId) {
    try {
      const entryRef = doc(db, 'pb-books', bookId, 'journal-entries', entryId);
      const docSnap = await getDoc(entryRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting journal entry:', error);
      throw error;
    }
  },

  /**
   * Save (create or update) a journal entry
   */
  async saveJournalEntry(bookId, entry) {
    try {
      const entryId = entry.id || doc(collection(db, 'pb-books', bookId, 'journal-entries')).id;
      const entryRef = doc(db, 'pb-books', bookId, 'journal-entries', entryId);
      
      const entryData = {
        ...entry,
        id: entryId,
        bookId,
        createdBy: entry.createdBy || auth.currentUser?.uid,
        creatorEmail: entry.creatorEmail || auth.currentUser?.email,
        workspaceId: entry.workspaceId || null,
        visibility: entry.visibility || 'workspace',
        sharedWith: entry.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!entry.id) {
        entryData.createdAt = serverTimestamp();
      }
      
      await setDoc(entryRef, entryData, { merge: true });
      return entryId;
    } catch (error) {
      console.error('Error saving journal entry:', error);
      throw error;
    }
  },

  /**
   * Delete a journal entry
   */
  async deleteJournalEntry(bookId, entryId) {
    try {
      const entryRef = doc(db, 'pb-books', bookId, 'journal-entries', entryId);
      await deleteDoc(entryRef);
      return true;
    } catch (error) {
      console.error('Error deleting journal entry:', error);
      throw error;
    }
  },

  /**
   * Get journal entries by domain
   */
  async getJournalEntriesByDomain(bookId, domain) {
    try {
      const entriesRef = collection(db, 'pb-books', bookId, 'journal-entries');
      const q = query(
        entriesRef, 
        where('domain', '==', domain),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting journal entries by domain:', error);
      return [];
    }
  },

  /**
   * Get journal entries for a date range
   */
  async getJournalEntriesInRange(bookId, startDate, endDate) {
    try {
      const entriesRef = collection(db, 'pb-books', bookId, 'journal-entries');
      const q = query(
        entriesRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting journal entries in range:', error);
      return [];
    }
  },

  /**
   * Get pinned journal entries
   */
  async getPinnedJournalEntries(bookId) {
    try {
      const entriesRef = collection(db, 'pb-books', bookId, 'journal-entries');
      const q = query(
        entriesRef,
        where('isPinned', '==', true),
        orderBy('date', 'desc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pinned journal entries:', error);
      return [];
    }
  },

  // ============================================================
  // DIARY SETTINGS
  // ============================================================

  /**
   * Get diary settings from the book document
   */
  async getUserSettings(bookId, userId = null) {
    try {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) {
        console.warn('No user ID provided for settings');
        return {};
      }

      const settingsRef = doc(db, 'pb-books', bookId, 'user-settings', uid);
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      
      // Return default settings if none exist
      return this.getDefaultUserSettings(uid);
    } catch (error) {
      console.error('Error getting user settings:', error);
      return this.getDefaultUserSettings(userId || auth.currentUser?.uid);
    }
  },

  /**
   * Save user settings
   */
  async saveUserSettings(bookId, settings, userId = null) {
    try {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) {
        throw new Error('User must be authenticated to save settings');
      }

      const settingsRef = doc(db, 'pb-books', bookId, 'user-settings', uid);
      
      const settingsData = {
        ...settings,
        userId: uid,
        userEmail: auth.currentUser?.email,
        updatedAt: serverTimestamp()
      };
      
      // Check if settings exist
      const docSnap = await getDoc(settingsRef);
      
      if (!docSnap.exists()) {
        settingsData.createdAt = serverTimestamp();
      }
      
      await setDoc(settingsRef, settingsData, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error saving user settings:', error);
      throw error;
    }
  },

  /**
   * Get default user settings
   */
  getDefaultUserSettings(userId) {
    return {
      userId: userId || '',
      userEmail: auth.currentUser?.email || '',
      defaultView: 'DASHBOARD',
      defaultMeetingDuration: 60,
      weekStartsOn: 'monday',
      timeFormat: '24h',
      theme: 'light',
      enableNotifications: true,
      reminderDefault: 15,
      aiContext: '',
      aiPersonality: 'professional',
      enableAISuggestions: true,
      geminiApiKey: '',
      defaultVisibility: 'private',
      enabledDomains: ['PERSONAL', 'WORK', 'HEALTH'],
      enabledContactCategories: ['WORK', 'FRIEND', 'FAMILY', 'SERVICE']
    };
  },

  /**
   * Delete user settings
   */
  async deleteUserSettings(bookId, userId = null) {
    try {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) {
        throw new Error('User must be authenticated');
      }

      const settingsRef = doc(db, 'pb-books', bookId, 'user-settings', uid);
      await deleteDoc(settingsRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting user settings:', error);
      throw error;
    }
  },

  /**
   * Get all user settings in a diary (for admins)
   */
  async getAllUserSettings(bookId) {
    try {
      const settingsRef = collection(db, 'pb-books', bookId, 'user-settings');
      const snapshot = await getDocs(settingsRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all user settings:', error);
      return [];
    }
  },

  // ============================================================
  // ORGANIZATION SETTINGS (Organization-wide Preferences)
  // ============================================================

  /**
   * Get organization settings for this diary
   */
  async getOrganizationSettings(bookId, organizationId) {
    try {
      if (!organizationId) {
        return {};
      }

      const settingsRef = doc(db, 'pb-books', bookId, 'org-settings', organizationId);
      const docSnap = await getDoc(settingsRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      
      return this.getDefaultOrganizationSettings(organizationId);
    } catch (error) {
      console.error('Error getting organization settings:', error);
      return this.getDefaultOrganizationSettings(organizationId);
    }
  },

  /**
   * Save organization settings
   */
  async saveOrganizationSettings(bookId, organizationId, settings) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const settingsRef = doc(db, 'pb-books', bookId, 'org-settings', organizationId);
      
      const settingsData = {
        ...settings,
        organizationId,
        updatedAt: serverTimestamp()
      };
      
      const docSnap = await getDoc(settingsRef);
      
      if (!docSnap.exists()) {
        settingsData.createdAt = serverTimestamp();
      }
      
      await setDoc(settingsRef, settingsData, { merge: true });
      
      return true;
    } catch (error) {
      console.error('Error saving organization settings:', error);
      throw error;
    }
  },

  /**
   * Get default organization settings
   */
  getDefaultOrganizationSettings(organizationId) {
    return {
      organizationId: organizationId || '',
      workingHoursStart: '09:00',
      workingHoursEnd: '17:00',
      workingDays: [1, 2, 3, 4, 5],
      requireMeetingApproval: false,
      maxMeetingDuration: 240,
      defaultMeetingLocation: '',
      timezone: 'UTC'
    };
  },

  // ============================================================
  // PLANNER ITEMS (Tasks, Events, Bookings)
  // ============================================================
  /**
   * Get a single planner item
   */
  async getPlannerItem(bookId, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'planner-items', itemId);
      const docSnap = await getDoc(itemRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting planner item:', error);
      throw error;
    }
  },

  /**
   * Save (create or update) a planner item
   */
  async savePlannerItem(bookId, item) {
    try {
      const itemId = item.id || doc(collection(db, 'pb-books', bookId, 'planner-items')).id;
      const itemRef = doc(db, 'pb-books', bookId, 'planner-items', itemId);
      
      const itemData = {
        ...item,
        id: itemId,
        bookId,
        createdBy: item.createdBy || auth.currentUser?.uid,
        creatorEmail: item.creatorEmail || auth.currentUser?.email,
        workspaceId: item.workspaceId || null,
        visibility: item.visibility || 'workspace',
        sharedWith: item.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!item.id) {
        itemData.createdAt = serverTimestamp();
      }
      
      await setDoc(itemRef, itemData, { merge: true });
      return itemId;
    } catch (error) {
      console.error('Error saving planner item:', error);
      throw error;
    }
  },

  /**
   * Delete a planner item
   */
  async deletePlannerItem(bookId, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'planner-items', itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error('Error deleting planner item:', error);
      throw error;
    }
  },

  /**
   * Get planner items by type
   */
  async getPlannerItemsByType(bookId, type) {
    try {
      const itemsRef = collection(db, 'pb-books', bookId, 'planner-items');
      const q = query(
        itemsRef,
        where('type', '==', type),
        orderBy('date', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting planner items by type:', error);
      return [];
    }
  },

  /**
   * Get planner items for date range
   */
  async getPlannerItemsInRange(bookId, startDate, endDate) {
    try {
      const itemsRef = collection(db, 'pb-books', bookId, 'planner-items');
      const q = query(
        itemsRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'asc'),
        orderBy('time', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting planner items in range:', error);
      return [];
    }
  },

  /**
   * Get items with active reminders
   */
  async getItemsWithReminders(bookId) {
    try {
      const items = await this.getPlannerItems(bookId);
      return items.filter(item => 
        item.reminders?.length > 0 && 
        item.reminders.some(r => !r.dismissed)
      );
    } catch (error) {
      console.error('Error getting items with reminders:', error);
      return [];
    }
  },

  /**
   * Dismiss a reminder
   */
  async dismissReminder(bookId, itemId, reminderId) {
    try {
      const item = await this.getPlannerItem(bookId, itemId);
      if (!item) throw new Error('Item not found');
      
      const updatedReminders = (item.reminders || []).map(r => 
        r.id === reminderId ? { ...r, dismissed: true } : r
      );
      
      await this.savePlannerItem(bookId, { ...item, reminders: updatedReminders });
      return true;
    } catch (error) {
      console.error('Error dismissing reminder:', error);
      throw error;
    }
  },

  /**
   * Update RSVP for an event
   */
  async updateEventRSVP(bookId, itemId, rsvp) {
    try {
      const item = await this.getPlannerItem(bookId, itemId);
      if (!item) throw new Error('Item not found');
      
      const existingRsvps = item.rsvps || [];
      const existingIndex = existingRsvps.findIndex(r => r.odId === rsvp.userId);
      
      let updatedRsvps;
      if (existingIndex >= 0) {
        updatedRsvps = existingRsvps.map((r, i) => 
          i === existingIndex ? { ...r, ...rsvp, updatedAt: new Date().toISOString() } : r
        );
      } else {
        updatedRsvps = [...existingRsvps, { ...rsvp, createdAt: new Date().toISOString() }];
      }
      
      await this.savePlannerItem(bookId, { ...item, rsvps: updatedRsvps });
      return true;
    } catch (error) {
      console.error('Error updating RSVP:', error);
      throw error;
    }
  },

  /**
   * Update booking status
   */
  async updateBookingStatus(bookId, itemId, status) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'planner-items', itemId);
      await updateDoc(itemRef, {
        status,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  },

  // ============================================================
  // DATA MANAGEMENT
  // ============================================================

  /**
   * Reset all diary data (delete all meetings, contacts, journal entries, templates)
   */
  async resetData(bookId) {
    try {
      const batch = writeBatch(db);
      
      // Delete all meetings
      const meetingsSnapshot = await getDocs(collection(db, 'pb-books', bookId, 'meetings'));
      meetingsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      // Delete all contacts
      const contactsSnapshot = await getDocs(collection(db, 'pb-books', bookId, 'contacts'));
      contactsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      // Delete all journal entries
      const journalSnapshot = await getDocs(collection(db, 'pb-books', bookId, 'journal-entries'));
      journalSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      // Delete all templates
      const templatesSnapshot = await getDocs(collection(db, 'pb-books', bookId, 'meeting-templates'));
      templatesSnapshot.docs.forEach(doc => batch.delete(doc.ref));
      
      // Reset settings
      const bookRef = doc(db, 'pb-books', bookId);
      batch.update(bookRef, {
        diarySettings: {},
        updatedAt: serverTimestamp()
      });
      
      await batch.commit();
      
      return true;
    } catch (error) {
      console.error('Error resetting diary data:', error);
      throw error;
    }
  },

  /**
   * Export all diary data as JSON
   */
  async exportData(bookId) {
    try {
      const [meetings, contacts, journalEntries, templates, settings] = await Promise.all([
        this.getMeetings(bookId),
        this.getContacts(bookId),
        this.getJournalEntries(bookId),
        this.getMeetingTemplates(bookId),
        this.getSettings(bookId)
      ]);
      
      return {
        meetings,
        contacts,
        journalEntries,
        templates,
        settings,
        exportedAt: new Date().toISOString(),
        bookId
      };
    } catch (error) {
      console.error('Error exporting diary data:', error);
      throw error;
    }
  },

  /**
   * Import diary data from JSON
   */
  async importData(bookId, data) {
    try {
      const batch = writeBatch(db);
      
      // Import meetings
      if (data.meetings?.length) {
        for (const meeting of data.meetings) {
          const meetingRef = doc(db, 'pb-books', bookId, 'meetings', meeting.id);
          batch.set(meetingRef, {
            ...meeting,
            importedAt: serverTimestamp()
          });
        }
      }
      
      // Import contacts
      if (data.contacts?.length) {
        for (const contact of data.contacts) {
          const contactRef = doc(db, 'pb-books', bookId, 'contacts', contact.id);
          batch.set(contactRef, {
            ...contact,
            importedAt: serverTimestamp()
          });
        }
      }
      
      // Import journal entries
      if (data.journalEntries?.length) {
        for (const entry of data.journalEntries) {
          const entryRef = doc(db, 'pb-books', bookId, 'journal-entries', entry.id);
          batch.set(entryRef, {
            ...entry,
            importedAt: serverTimestamp()
          });
        }
      }
      
      // Import templates
      if (data.templates?.length) {
        for (const template of data.templates) {
          const templateRef = doc(db, 'pb-books', bookId, 'meeting-templates', template.id);
          batch.set(templateRef, {
            ...template,
            importedAt: serverTimestamp()
          });
        }
      }
      
      // Import settings
      if (data.settings) {
        const bookRef = doc(db, 'pb-books', bookId);
        batch.update(bookRef, {
          diarySettings: data.settings,
          updatedAt: serverTimestamp()
        });
      }
      
      await batch.commit();
      
      return true;
    } catch (error) {
      console.error('Error importing diary data:', error);
      throw error;
    }
  },

  // ============================================================
  // FILE UPLOADS
  // ============================================================

  /**
   * Upload a meeting recording
   */
  async uploadMeetingRecording(bookId, meetingId, file) {
    try {
      const path = `pb-${bookId}/meetings/${meetingId}/recordings`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading meeting recording:', error);
      throw error;
    }
  },

  /**
   * Upload a meeting attachment
   */
  async uploadMeetingAttachment(bookId, meetingId, file) {
    try {
      const path = `pb-${bookId}/meetings/${meetingId}/attachments`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading meeting attachment:', error);
      throw error;
    }
  },

  /**
   * Upload a journal entry attachment
   */
  async uploadJournalAttachment(bookId, entryId, file) {
    try {
      const path = `pb-${bookId}/journal/${entryId}/attachments`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading journal attachment:', error);
      throw error;
    }
  },

  /**
   * Upload a contact avatar
   */
  async uploadContactAvatar(bookId, contactId, file) {
    try {
      const path = `pb-${bookId}/contacts/${contactId}`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading contact avatar:', error);
      throw error;
    }
  },

  // ============================================================
  // MEDIA ITEMS (Documents, Videos, etc.)
  // ============================================================

  async saveMediaItem(bookId, item) {
    try {
      const itemId = item.id || doc(collection(db, 'pb-books', bookId, 'media-items')).id;
      const itemRef = doc(db, 'pb-books', bookId, 'media-items', itemId);
      
      const itemData = {
        ...item,
        id: itemId,
        bookId,
        createdBy: item.createdBy || auth.currentUser?.uid,
        creatorEmail: item.creatorEmail || auth.currentUser?.email,
        workspaceId: item.workspaceId || null,
        visibility: item.visibility || 'workspace',
        sharedWith: item.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!item.id) {
        itemData.createdAt = serverTimestamp();
      }
      
      await setDoc(itemRef, itemData, { merge: true });
      return itemId;
    } catch (error) {
      console.error('Error saving media item:', error);
      throw error;
    }
  },

  async deleteMediaItem(bookId, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'media-items', itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error('Error deleting media item:', error);
      throw error;
    }
  },

  // ============================================================
  // ASSETS (Vehicles, Properties, Shares, etc.)
  // ============================================================

  async saveAsset(bookId, asset) {
    try {
      const assetId = asset.id || doc(collection(db, 'pb-books', bookId, 'assets')).id;
      const assetRef = doc(db, 'pb-books', bookId, 'assets', assetId);
      
      const assetData = {
        ...asset,
        id: assetId,
        bookId,
        createdBy: asset.createdBy || auth.currentUser?.uid,
        creatorEmail: asset.creatorEmail || auth.currentUser?.email,
        workspaceId: asset.workspaceId || null,
        visibility: asset.visibility || 'shared',
        sharedWith: asset.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!asset.id) {
        assetData.createdAt = serverTimestamp();
      }
      
      await setDoc(assetRef, assetData, { merge: true });
      return assetId;
    } catch (error) {
      console.error('Error saving asset:', error);
      throw error;
    }
  },

  async deleteAsset(bookId, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'assets', itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error('Error deleting asset:', error);
      throw error;
    }
  },

  async getAssetsByCategory(bookId, category) {
    try {
      const itemsRef = collection(db, 'pb-books', bookId, 'assets');
      const q = query(itemsRef, where('category', '==', category), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting assets by category:', error);
      return [];
    }
  },

  // ============================================================
  // DIGITAL TOOLS (Embeds, Links, HTML Widgets)
  // ============================================================

  async saveTool(bookId, tool) {
    try {
      const toolId = tool.id || doc(collection(db, 'pb-books', bookId, 'tools')).id;
      const toolRef = doc(db, 'pb-books', bookId, 'tools', toolId);
      
      const toolData = {
        ...tool,
        id: toolId,
        bookId,
        createdBy: tool.createdBy || auth.currentUser?.uid,
        creatorEmail: tool.creatorEmail || auth.currentUser?.email,
        workspaceId: tool.workspaceId || null,
        visibility: tool.visibility || 'workspace',
        sharedWith: tool.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      if (!tool.id) {
        toolData.createdAt = serverTimestamp();
      }
      
      await setDoc(toolRef, toolData, { merge: true });
      return toolId;
    } catch (error) {
      console.error('Error saving tool:', error);
      throw error;
    }
  },

  async deleteTool(bookId, itemId) {
    try {
      const itemRef = doc(db, 'pb-books', bookId, 'tools', itemId);
      await deleteDoc(itemRef);
      return true;
    } catch (error) {
      console.error('Error deleting tool:', error);
      throw error;
    }
  },

  /**
 * Generic method to get user's accessible documents from any subcollection
 */
  async getUserAccessibleDocuments(bookId, subcollection, orderByField = 'createdAt') {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return [];
      
      // Check if user is book owner
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) return [];
      
      const isOwner = bookSnap.data().ownerId === currentUser.uid;
      
      const collectionRef = collection(db, 'pb-books', bookId, subcollection);
      
      // If owner, get all documents
      if (isOwner) {
        const q = query(collectionRef, orderBy(orderByField, 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      // Non-owner: query by createdBy AND sharedWithUsers
      const [myDocs, sharedDocs] = await Promise.all([
        // Documents I created
        getDocs(query(
          collectionRef,
          where('createdBy', '==', currentUser.uid),
          orderBy(orderByField, 'desc')
        )),
        // Documents shared with me
        getDocs(query(
          collectionRef,
          where('sharedWithUsers', 'array-contains', currentUser.uid),
          orderBy(orderByField, 'desc')
        ))
      ]);
      
      // Combine and deduplicate
      const docsMap = new Map();
      myDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      sharedDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      
      return Array.from(docsMap.values());
    } catch (error) {
      console.error(`Error getting ${subcollection}:`, error);
      return [];
    }
  },

  async getMeetings(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'meetings', 'date');
  },

  async getContacts(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'contacts', 'name');
  },

  async getJournalEntries(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'journal-entries', 'createdAt');
  },

  async getRecords(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'records', 'startDate');
  },

  async getPlannerItems(bookId) {
    // Special handling for planner items - use startTime if available
    try {
      const items = await diaryService.getUserAccessibleDocuments(bookId, 'planner-items', 'date');
      
      // Sort by startTime in memory (since some items may not have startTime)
      return items.sort((a, b) => {
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        const timeA = a.startTime || a.time || '00:00';
        const timeB = b.startTime || b.time || '00:00';
        return timeA.localeCompare(timeB);
      });
    } catch (error) {
      console.error('Error getting planner items:', error);
      return [];
    }
  },

  async getMediaItems(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'media-items', 'createdAt');
  },

  async getAssets(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'assets', 'createdAt');
  },

  async getTools(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'tools', 'createdAt');
  },

  async getMeetingTemplates(bookId) {
    return diaryService.getUserAccessibleDocuments(bookId, 'meeting-templates', 'name');
  },

  /**
   * Check if user can access a specific document
   */
  async canUserAccessDocument(bookId, subcollection, documentId) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return { canAccess: false, permissions: null };
      
      const docRef = doc(db, 'pb-books', bookId, subcollection, documentId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return { canAccess: false, permissions: null };
      }
      
      const docData = docSnap.data();
      
      // Check if user is creator
      if (docData.createdBy === currentUser.uid) {
        return {
          canAccess: true,
          permissions: {
            canView: true,
            canEdit: true,
            canDelete: true,
            canShare: true,
            canComment: true,
            role: 'creator'
          }
        };
      }
      
      // Check if document is public
      if (docData.visibility === 'public') {
        return {
          canAccess: true,
          permissions: {
            canView: true,
            canEdit: false,
            canDelete: false,
            canShare: false,
            canComment: true,
            role: 'viewer'
          }
        };
      }
      
      // Check if shared with user
      const userShare = docData.sharedWith?.find(s => s.userId === currentUser.uid);
      if (userShare) {
        return {
          canAccess: true,
          permissions: {
            ...userShare.permissions,
            role: userShare.role
          }
        };
      }
      
      // Check if book owner
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      if (bookSnap.exists() && bookSnap.data().ownerId === currentUser.uid) {
        return {
          canAccess: true,
          permissions: {
            canView: true,
            canEdit: true,
            canDelete: true,
            canShare: true,
            canComment: true,
            role: 'owner'
          }
        };
      }
      
      return { canAccess: false, permissions: null };
    } catch (error) {
      console.error('Error checking document access:', error);
      return { canAccess: false, permissions: null };
    }
  },

  async uploadRecordAttachment(bookId, recordId, file) {
    try {
      const path = `pb-${bookId}/records/${recordId}/attachments`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading record attachment:', error);
      throw error;
    }
  },

  async getRecord(bookId, recordId) {
    try {
      const recordRef = doc(db, 'pb-books', bookId, 'records', recordId);
      const docSnap = await getDoc(recordRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting record:', error);
      throw error;
    }
  },

  /**
   * Save (create or update) a record
   */
  async saveRecord(bookId, record) {
    try {
      const recordId = record.id || doc(collection(db, 'pb-books', bookId, 'records')).id;
      const recordRef = doc(db, 'pb-books', bookId, 'records', recordId);
      
      const recordData = {
        ...record,
        id: recordId,
        bookId,
        createdBy: record.createdBy || auth.currentUser?.uid,
        creatorEmail: record.creatorEmail || auth.currentUser?.email,
        workspaceId: record.workspaceId || null,
        visibility: record.visibility || 'workspace',
        sharedWith: record.sharedWith || [],
        updatedAt: serverTimestamp()
      };
      
      // Only set createdAt for new documents
      if (!record.id) {
        recordData.createdAt = serverTimestamp();
      }
      
      await setDoc(recordRef, recordData, { merge: true });
      
      return recordId;
    } catch (error) {
      console.error('Error saving record:', error);
      throw error;
    }
  },

  /**
   * Delete a record
   */
  async deleteRecord(bookId, recordId) {
    try {
      const recordRef = doc(db, 'pb-books', bookId, 'records', recordId);
      await deleteDoc(recordRef);
      return true;
    } catch (error) {
      console.error('Error deleting record:', error);
      throw error;
    }
  },

  /**
   * Get records by type
   */
  async getRecordsByType(bookId, type) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return [];
      
      // Check if user is book owner
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) return [];
      
      const isOwner = bookSnap.data().ownerId === currentUser.uid;
      
      const recordsRef = collection(db, 'pb-books', bookId, 'records');
      
      // If owner, get all records of this type
      if (isOwner) {
        const q = query(
          recordsRef,
          where('type', '==', type),
          orderBy('startDate', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      // Non-owner: query by type AND (createdBy OR sharedWithUsers)
      const [myDocs, sharedDocs] = await Promise.all([
        getDocs(query(
          recordsRef,
          where('type', '==', type),
          where('createdBy', '==', currentUser.uid),
          orderBy('startDate', 'desc')
        )),
        getDocs(query(
          recordsRef,
          where('type', '==', type),
          where('sharedWithUsers', 'array-contains', currentUser.uid),
          orderBy('startDate', 'desc')
        ))
      ]);
      
      // Combine and deduplicate
      const docsMap = new Map();
      myDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      sharedDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      
      return Array.from(docsMap.values());
    } catch (error) {
      console.error('Error getting records by type:', error);
      return [];
    }
  },

  /**
   * Get records in date range
   */
  async getRecordsInRange(bookId, startDate, endDate) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return [];
      
      // Check if user is book owner
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) return [];
      
      const isOwner = bookSnap.data().ownerId === currentUser.uid;
      
      const recordsRef = collection(db, 'pb-books', bookId, 'records');
      
      // If owner, get all records in range
      if (isOwner) {
        const q = query(
          recordsRef,
          where('startDate', '>=', startDate),
          where('startDate', '<=', endDate),
          orderBy('startDate', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      // Non-owner: query by date range AND (createdBy OR sharedWithUsers)
      const [myDocs, sharedDocs] = await Promise.all([
        getDocs(query(
          recordsRef,
          where('startDate', '>=', startDate),
          where('startDate', '<=', endDate),
          where('createdBy', '==', currentUser.uid),
          orderBy('startDate', 'desc')
        )),
        getDocs(query(
          recordsRef,
          where('startDate', '>=', startDate),
          where('startDate', '<=', endDate),
          where('sharedWithUsers', 'array-contains', currentUser.uid),
          orderBy('startDate', 'desc')
        ))
      ]);
      
      // Combine and deduplicate
      const docsMap = new Map();
      myDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      sharedDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      
      return Array.from(docsMap.values());
    } catch (error) {
      console.error('Error getting records in range:', error);
      return [];
    }
  },

  /**
   * Search records
   */
  async searchRecords(bookId, searchTerm) {
    try {
      const records = await this.getRecords(bookId);
      const term = searchTerm.toLowerCase();
      
      return records.filter(r => 
        r.title?.toLowerCase().includes(term) ||
        r.description?.toLowerCase().includes(term) ||
        (r.tags || []).some(t => t.toLowerCase().includes(term)) ||
        Object.values(r.context || {}).some(v => 
          String(v).toLowerCase().includes(term)
        )
      );
    } catch (error) {
      console.error('Error searching records:', error);
      return [];
    }
  },

  /**
   * Get pinned records
   */
  async getPinnedRecords(bookId) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return [];
      
      // Check if user is book owner
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) return [];
      
      const isOwner = bookSnap.data().ownerId === currentUser.uid;
      
      const recordsRef = collection(db, 'pb-books', bookId, 'records');
      
      // If owner, get all pinned records
      if (isOwner) {
        const q = query(
          recordsRef,
          where('isPinned', '==', true),
          orderBy('startDate', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      // Non-owner: query by pinned AND (createdBy OR sharedWithUsers)
      const [myDocs, sharedDocs] = await Promise.all([
        getDocs(query(
          recordsRef,
          where('isPinned', '==', true),
          where('createdBy', '==', currentUser.uid),
          orderBy('startDate', 'desc')
        )),
        getDocs(query(
          recordsRef,
          where('isPinned', '==', true),
          where('sharedWithUsers', 'array-contains', currentUser.uid),
          orderBy('startDate', 'desc')
        ))
      ]);
      
      // Combine and deduplicate
      const docsMap = new Map();
      myDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      sharedDocs.docs.forEach(doc => docsMap.set(doc.id, { id: doc.id, ...doc.data() }));
      
      return Array.from(docsMap.values());
    } catch (error) {
      console.error('Error getting pinned records:', error);
      return [];
    }
  },

  async shareRecord(bookId, recordId, userIds, permissions = { canView: true, canEdit: false }) {
    try {
      const recordRef = doc(db, 'pb-books', bookId, 'records', recordId);
      const recordSnap = await getDoc(recordRef);
      
      if (!recordSnap.exists()) {
        throw new Error('Record not found');
      }
      
      const currentSharedWith = recordSnap.data().sharedWith || [];
      const currentSharedUsers = recordSnap.data().sharedWithUsers || [];
      
      const newShares = userIds.map(userId => ({
        userId,
        permissions,
        sharedAt: new Date().toISOString()
      }));
      
      await updateDoc(recordRef, {
        sharedWith: [...currentSharedWith, ...newShares],
        sharedWithUsers: [...new Set([...currentSharedUsers, ...userIds])],
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error sharing record:', error);
      throw error;
    }
  },

  async updateRecordVisibility(bookId, recordId, visibility) {
    try {
      const recordRef = doc(db, 'pb-books', bookId, 'records', recordId);
      await updateDoc(recordRef, {
        visibility,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating record visibility:', error);
      throw error;
    }
  },

  // ============================================================
  // ANALYTICS / STATS
  // ============================================================

  /**
   * Get diary statistics
   */
  async getStats(bookId) {
    try {
      const [meetings, contacts, journalEntries] = await Promise.all([
        this.getMeetings(bookId),
        this.getContacts(bookId),
        this.getJournalEntries(bookId)
      ]);
      
      const now = new Date();
      const thisWeekStart = new Date(now);
      thisWeekStart.setDate(now.getDate() - now.getDay());
      thisWeekStart.setHours(0, 0, 0, 0);
      
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      return {
        meetings: {
          total: meetings.length,
          planned: meetings.filter(m => m.status === 'PLANNED').length,
          active: meetings.filter(m => m.status === 'ACTIVE').length,
          completed: meetings.filter(m => m.status === 'COMPLETED').length,
          thisWeek: meetings.filter(m => new Date(m.date) >= thisWeekStart).length,
          thisMonth: meetings.filter(m => new Date(m.date) >= thisMonthStart).length
        },
        contacts: {
          total: contacts.length,
          favorites: contacts.filter(c => c.isFavorite).length,
          byCategory: contacts.reduce((acc, c) => {
            acc[c.category] = (acc[c.category] || 0) + 1;
            return acc;
          }, {})
        },
        journal: {
          total: journalEntries.length,
          pinned: journalEntries.filter(e => e.isPinned).length,
          thisWeek: journalEntries.filter(e => new Date(e.date) >= thisWeekStart).length,
          thisMonth: journalEntries.filter(e => new Date(e.date) >= thisMonthStart).length,
          byDomain: journalEntries.reduce((acc, e) => {
            acc[e.domain] = (acc[e.domain] || 0) + 1;
            return acc;
          }, {})
        },
        tasks: {
          total: meetings.reduce((sum, m) => sum + (m.minutes?.filter(min => min.type === 'TASK').length || 0), 0),
          completed: meetings.reduce((sum, m) => 
            sum + (m.minutes?.filter(min => min.type === 'TASK' && min.completed).length || 0), 0
          ),
          overdue: meetings.reduce((sum, m) => 
            sum + (m.minutes?.filter(min => 
              min.type === 'TASK' && 
              !min.completed && 
              min.dueDate && 
              new Date(min.dueDate) < now
            ).length || 0), 0
          )
        }
      };
    } catch (error) {
      console.error('Error getting diary stats:', error);
      return {
        meetings: { total: 0, planned: 0, active: 0, completed: 0 },
        contacts: { total: 0, favorites: 0, byCategory: {} },
        journal: { total: 0, pinned: 0, byDomain: {} },
        tasks: { total: 0, completed: 0, overdue: 0 }
      };
    }
  },

  /**
   * Get upcoming reminders (meetings in next 24 hours)
   */
  async getUpcomingReminders(bookId, hoursAhead = 24) {
    try {
      const now = new Date();
      const futureDate = new Date(now.getTime() + hoursAhead * 60 * 60 * 1000);
      
      const todayStr = now.toISOString().split('T')[0];
      const futureStr = futureDate.toISOString().split('T')[0];
      
      const meetings = await this.getMeetingsInRange(bookId, todayStr, futureStr);
      
      return meetings.filter(m => {
        if (m.status !== 'PLANNED') return false;
        const meetingDateTime = new Date(`${m.date}T${m.time}`);
        return meetingDateTime >= now && meetingDateTime <= futureDate;
      }).sort((a, b) => {
        const aTime = new Date(`${a.date}T${a.time}`);
        const bTime = new Date(`${b.date}T${b.time}`);
        return aTime - bTime;
      });
    } catch (error) {
      console.error('Error getting upcoming reminders:', error);
      return [];
    }
  }
};

export default diaryService;